import { styled } from "@mui/system";
import hammerIcon from '../../../assets/icons/hammer.svg';
import forgeLogo from '../../../assets/icons/forge.svg';
import ellipse from '../../../assets/icons/ellipse.svg';
import ellipseOuter from '../../../assets/icons/ellipse-outer.svg';
import umakeLogo from '../../../assets/icons/umake.svg';
import contactEllipses from '../../../assets/icons/contact-ellipses.svg';
import { Button } from "../../common/Button/Button";
import { motion } from 'framer-motion';
import { useTranslation } from "react-i18next";

const ContactUsContainer = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '90px',
    padding: '96px 0',
    position: 'relative',
    '@media (max-width: 1024px)': {
        flexDirection: 'column',
        gap: '48px',
        padding: '48px 16px 84px 16px',
    }
})

const HammerIcon = styled('img')({
    width: '105px',
    height: '80px',
    '@media (max-width: 1024px)': {
        width: '80px',
        height: '60px',
    }
})

const ForgeIcon = styled('img')({
    width: '172px',
    height: '47px',
    '@media (max-width: 1024px)': {
        width: '140px',
        height: '38px',
    }
})

const ForgeLogoContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    '@media (max-width: 1024px)': {
        gap: '12px',
    }
})

const AndContainer = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    position: 'relative',
    width: '120px',
    height: '120px',
    '@media (max-width: 1024px)': {
        width: '100px',
        height: '100px',
    }
})

const Ellipse = styled('img')({
    width: '120px',
    height: '120px',
    position: 'absolute',
    top: '0',
    left: '0',
    '@media (max-width: 1024px)': {
        width: '100px',
        height: '100px',
    }
})

const EllipseOuter = styled('img')({
    width: '130px',
    height: '130px',
    position: 'absolute',
    top: '-5px',
    left: '-5px',
    '@media (max-width: 1024px)': {
        width: '110px',
        height: '110px',
    }
})

const AndText = styled('p')({
    color: '#00E378',
    textAlign: 'center',
    fontSize: '48px',
    fontWeight: '800',
    lineHeight: '54px',
    '@media (max-width: 1024px)': {
        fontSize: '36px',
        lineHeight: '40px',
    }
})

const UmakeLogo = styled('img')({
    width: '218px',
    height: '152px',
    '@media (max-width: 1024px)': {
        width: '160px',
        height: '112px',
    }
})

const ContactBottomContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    position: 'absolute',
    bottom: '-270px',
    width: '640px',
    '@media (max-width: 1024px)': {
        position: 'absolute',
        width: '100%',
        marginTop: '48px',
        bottom: '-170px'
    }
})

const ContactEllipses = styled('img')({
    width: '566px',
    height: '300px',
    '@media (max-width: 1024px)': {
        width: '100%',
        height: 'auto',
        maxHeight: '200px',
    }
})

const ContactUsSubContainer = styled('div')({
    display: 'flex',
    position: 'absolute',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    justifyContent: 'center',
    zIndex: 2,

})

const ContactUsTitleContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',

    '@media (max-width: 1024px)': {
        width: '100%',
    }
})

const ContactUsTitle = styled('p')({
    color: '#00E378',
    textAlign: 'center',
    fontSize: '64px',
    fontWeight: '800',
    lineHeight: '91px',
    whiteSpace: 'nowrap',
    '@media (max-width: 1024px)': {
        fontSize: '36px',
        lineHeight: '48px',
        whiteSpace: 'normal',
    }
})

const ContactUsSubtitle = styled('p')({
    color: '#F1F7F7',
    textAlign: 'center',
    fontSize: '48px',
    fontWeight: '700',
    lineHeight: '64px',
    whiteSpace: 'nowrap',
    '@media (max-width: 1024px)': {
        fontSize: '24px',
        lineHeight: '32px',
    }
})

export const ContactUs = () => {
    const { t } = useTranslation();

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    const scaleVariants = {
        hidden: { opacity: 0, scale: 0.8 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    return (
        <ContactUsContainer>
            <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
            >
                <ForgeLogoContainer>
                    <motion.div variants={itemVariants}>
                        <HammerIcon src={hammerIcon} alt="Hammer Icon" />
                    </motion.div>
                    <motion.div variants={itemVariants}>
                        <ForgeIcon src={forgeLogo} alt="Forge Logo" />
                    </motion.div>
                </ForgeLogoContainer>
            </motion.div>

            <motion.div
                variants={scaleVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
            >
                <AndContainer>
                    <Ellipse src={ellipse} alt="Ellipse" />
                    <EllipseOuter src={ellipseOuter} alt="Ellipse Outer" />
                    <AndText>&</AndText>
                </AndContainer>
            </motion.div>

            <motion.div
                variants={itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
            >
                <UmakeLogo src={umakeLogo} alt="Umake Logo" />
            </motion.div>

            <ContactBottomContainer>
                <motion.div
                    variants={itemVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                >
                    <ContactEllipses src={contactEllipses} alt="Contact Ellipses" />
                </motion.div>
                <ContactUsSubContainer>
                    <motion.div
                        variants={itemVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <ContactUsTitleContainer>
                            <ContactUsTitle>{t('contactUs.title')}</ContactUsTitle>
                            <ContactUsSubtitle>{t('contactUs.subtitle')}</ContactUsSubtitle>
                        </ContactUsTitleContainer>
                    </motion.div>
                    <motion.div
                        variants={itemVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <Button onClick={() => window.open('https://qualif.forgeit.fr/form')}>{t('contactUs.button')}</Button>
                    </motion.div>
                </ContactUsSubContainer>
            </ContactBottomContainer>
        </ContactUsContainer>
    )
}
